import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../api/client';
import Modal from '../shared/Modal';
import { Wand2, AlertTriangle } from 'lucide-react';
import { cn } from '../../utils/cn';

export default function MasterPromptPanel({ shops, documents }) {
  const { t } = useTranslation();
  const [selectedShopId, setSelectedShopId] = useState('');
  const [status, setStatus] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!selectedShopId && shops.length > 0) setSelectedShopId(shops[0].id);
  }, [shops, selectedShopId]);

  const fetchStatus = useCallback(async () => {
    if (!selectedShopId) return;
    try {
      const res = await api.get(`/shops/${selectedShopId}/master-prompt`);
      setStatus(res.data.data);
    } catch (err) {
      console.error('Failed to fetch master prompt status:', err);
    }
  }, [selectedShopId]);

  useEffect(() => { fetchStatus(); }, [fetchStatus, documents]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    try {
      const res = await api.post(`/shops/${selectedShopId}/master-prompt/generate`, null, { timeout: 420000 });
      setDraft(res.data.data);
      setReviewOpen(true);
    } catch (err) {
      setError(err.response?.data?.message || t('documents.master_prompt_generate_failed'));
    } finally {
      setGenerating(false);
    }
  };

  const openStored = () => {
    setDraft({ content: status.content, source_hash: status.source_hash });
    setReviewOpen(true);
  };

  const handleActivate = async () => {
    setSaving(true);
    setError('');
    try {
      await api.put(`/shops/${selectedShopId}/master-prompt`, {
        content: draft.content,
        source_hash: draft.source_hash,
      });
      setReviewOpen(false);
      fetchStatus();
    } catch (err) {
      setError(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!window.confirm(t('documents.master_prompt_deactivate_confirm'))) return;
    try {
      await api.delete(`/shops/${selectedShopId}/master-prompt`);
      fetchStatus();
    } catch (err) {
      console.error('Deactivate failed:', err);
    }
  };

  if (shops.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Wand2 className="h-5 w-5 text-primary-600" />
            <h2 className="font-semibold text-gray-900">{t('documents.master_prompt_title')}</h2>
            {status && (
              <span className={cn(
                'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                status.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
              )}>
                {status.is_active ? t('documents.master_prompt_active') : t('documents.master_prompt_inactive')}
              </span>
            )}
            {status?.is_stale && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                <AlertTriangle className="h-3 w-3" /> {t('documents.master_prompt_outdated')}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-gray-500">{t('documents.master_prompt_hint')}</p>
          {status?.generated_at && (
            <p className="mt-1 text-xs text-gray-400">
              {t('documents.master_prompt_generated_at')}: {new Date(status.generated_at).toLocaleString()}
            </p>
          )}
          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        </div>

        <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
          {shops.length > 1 && (
            <select
              value={selectedShopId}
              onChange={(e) => setSelectedShopId(e.target.value)}
              className="rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
            >
              {shops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
          {status && (
            <button
              onClick={openStored}
              className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              {t('documents.master_prompt_edit')}
            </button>
          )}
          {status?.is_active && (
            <button
              onClick={handleDeactivate}
              className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              {t('documents.master_prompt_deactivate')}
            </button>
          )}
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors disabled:opacity-60"
          >
            {generating && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
            {generating
              ? t('documents.master_prompt_generating')
              : status ? t('documents.master_prompt_regenerate') : t('documents.master_prompt_generate')}
          </button>
        </div>
      </div>

      <Modal open={reviewOpen} onClose={() => setReviewOpen(false)} title={t('documents.master_prompt_review_title')} size="xl">
        <div className="space-y-4">
          <p className="text-sm text-gray-500">{t('documents.master_prompt_review_hint')}</p>
          <textarea
            value={draft?.content || ''}
            onChange={(e) => setDraft({ ...draft, content: e.target.value })}
            rows={20}
            className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 font-mono text-sm"
          />
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => setReviewOpen(false)}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleActivate}
              disabled={saving || !draft?.content?.trim()}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-60"
            >
              {t('documents.master_prompt_activate')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
